import { useCallback, useEffect, useState } from "react";

const DOCS_SEARCH_INDEX_URL = "https://docs.spacelift.io/search/search_index.json";
const SNIPPET_LENGTH = 200;

type DocsIndexEntry = {
  location: string;
  title: string;
  text: string;
};

type DocsSearchIndex = {
  docs: DocsIndexEntry[];
};

export type DocsSearchResult = {
  title: string;
  url: string;
  snippet: string;
};

type SearchDocsFunction = (input: { query: string; limit?: number }) => DocsSearchResult[];

let cachedIndex: DocsSearchIndex | undefined;
let fetchPromise: Promise<DocsSearchIndex> | undefined;

const fetchIndex = async (): Promise<DocsSearchIndex> => {
  if (!fetchPromise) {
    fetchPromise = fetch(DOCS_SEARCH_INDEX_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch docs search index: ${res.status}`);
        return res.json() as Promise<DocsSearchIndex>;
      })
      .catch((err) => {
        fetchPromise = undefined;
        throw err;
      });
  }
  return fetchPromise;
};

const buildSnippet = (text: string, query: string): string => {
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const idx = lowerText.indexOf(lowerQuery);

  if (idx === -1) {
    return text.slice(0, SNIPPET_LENGTH);
  }

  const start = Math.max(0, idx - 80);
  const end = Math.min(text.length, start + SNIPPET_LENGTH);
  const snippet = text.slice(start, end).trim();

  return (start > 0 ? "…" : "") + snippet + (end < text.length ? "…" : "");
};

const searchIndex = (index: DocsSearchIndex, query: string, limit: number): DocsSearchResult[] => {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];

  const scored = index.docs
    .map((entry) => {
      const titleLower = entry.title.toLowerCase();
      const textLower = entry.text.toLowerCase();
      let score = 0;

      for (const term of terms) {
        if (titleLower.includes(term)) score += 10;
        if (textLower.includes(term)) score += 1;
      }

      return { entry, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  return scored.map(({ entry }) => ({
    title: entry.title,
    url: `https://docs.spacelift.io/${entry.location}`,
    snippet: buildSnippet(entry.text, query),
  }));
};

const useSpaceliftDocsSearch = () => {
  const [index, setIndex] = useState<DocsSearchIndex | undefined>(cachedIndex);
  const [isLoading, setIsLoading] = useState(!cachedIndex);

  useEffect(() => {
    if (cachedIndex) return;

    let cancelled = false;

    const init = async () => {
      try {
        const data = await fetchIndex();
        if (!cancelled) {
          cachedIndex = data;
          setIndex(data);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  const searchDocs: SearchDocsFunction = useCallback(
    ({ query, limit = 5 }) => {
      if (!index) return [];
      return searchIndex(index, query, limit);
    },
    [index]
  );

  return { searchDocs, isLoading };
};

export default useSpaceliftDocsSearch;
